import React, { useState, useRef } from "react";
import { Button, Chip, Popover, Stack } from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { getFormattedDate } from "utils/date";

const NextSendDateRangeFilter = ({ column }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const currentFilter = column.getFilterValue();

  const resolveInitialDates = () => {
    if (currentFilter?.startDate && currentFilter?.endDate) {
      return {
        start: dayjs(currentFilter.startDate),
        end: dayjs(currentFilter.endDate),
      };
    }
    const today = dayjs();
    return { start: today, end: today };
  };

  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const handleOpen = () => {
    const { start, end } = resolveInitialDates();
    setDateRange({ start, end });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleApply = () => {
    const startDate = dateRange.start?.format("MM/DD/YYYY");
    const endDate = dateRange.end?.format("MM/DD/YYYY");
    const filterString = `${startDate} – ${endDate}`;

    column.setFilterValue({ startDate, endDate, toString: () => filterString });
    setOpen(false);
  };

  const handleClear = () => {
    column.setFilterValue(undefined);
    setOpen(false);
  };

  const isActive = !!currentFilter?.startDate;

  const displayLabel = isActive
    ? `${getFormattedDate(currentFilter.startDate, "MM/DD/YY")} – ${getFormattedDate(currentFilter.endDate, "MM/DD/YY")}`
    : null;

  return (
    <>
      <Stack
        ref={anchorRef}
        direction="row"
        alignItems="center"
        sx={{ width: "100%", py: 1, height: "100%" }}
      >
        {isActive ? (
          <Chip
            label={displayLabel}
            onClick={handleOpen}
            onDelete={handleClear}
            color="default"
            size="small"
            sx={{
              fontSize: "0.7rem",
              maxWidth: "100%",
              minHeight: 36,
              whiteSpace: "nowrap",
            }}
          />
        ) : (
          <Button
            size="small"
            startIcon={<FilterAlt />}
            onClick={handleOpen}
            color="secondary"
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Add Filter
          </Button>
        )}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2.5, minWidth: 300 } } }}
      >
        <Stack spacing={2}>
          <DatePicker
            label="Start Date"
            value={dateRange.start}
            onChange={(val) => {
              setDateRange((prev) => ({
                start: val,
                end: prev.end && val?.isAfter(prev.end) ? val : prev.end,
              }));
            }}
            maxDate={dateRange.end || undefined}
            slotProps={{
              textField: { fullWidth: true, required: true, readOnly: true },
            }}
          />

          <DatePicker
            label="End Date"
            value={dateRange.end}
            onChange={(val) => setDateRange((prev) => ({ ...prev, end: val }))}
            minDate={dateRange.start || undefined}
            slotProps={{
              textField: { fullWidth: true, required: true, readOnly: true },
            }}
          />

          <Stack direction="row" gap={2}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleApply}
              disabled={!dateRange.start || !dateRange.end}
            >
              Apply Filter
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default NextSendDateRangeFilter;
